import * as Yup from "yup";
import { validation, conditionalValidation } from "../../../utils/constants";

export const XML_FILE_TYPES = ["XML", "XML_HLBC_PHMS"];
export const SINGLE_XML_FORMAT_MESSAGE = "Only one XML format can be selected at a time.";

const fileTypeSchema = Yup.array()
    .of(Yup.string())
    .min(1, "File Type is a required field")
    .test("single-xml", SINGLE_XML_FORMAT_MESSAGE, (vals) => {
        if (!vals?.length) return true;
        return vals.filter((v) => XML_FILE_TYPES.includes(v)).length <= 1;
    });

/** Keep at most one XML format; prefer the XML type the user just added. */
export const resolveFileTypeSelection = (previous = [], selected = []) => {
    const next = selected.map((v) => v.value);
    const xmlInNext = next.filter((v) => XML_FILE_TYPES.includes(v));
    if (xmlInNext.length <= 1) {
        return { fileType: next, rejected: false };
    }
    const prevXml = previous.filter((v) => XML_FILE_TYPES.includes(v));
    const addedXml = xmlInNext.filter((v) => !prevXml.includes(v));
    const keepXml = addedXml.length ? addedXml[addedXml.length - 1] : xmlInNext[xmlInNext.length - 1];
    return {
        fileType: [...next.filter((v) => !XML_FILE_TYPES.includes(v)), keepXml],
        rejected: true,
    };
};

const protocolRequired = Yup.string().when("type", {
    is: (val) => val === "MFT" || val === "HL7",
    then: (schema) => schema.required("Protocol is Required"),
    otherwise: (schema) => schema.notRequired(),
});

const hostNameRequired = Yup.string().when("type", {
    is: (val) => val === "MFT" || val === "HL7",
    then: (schema) => schema.required("Hostname is Required"),
    otherwise: (schema) => schema.notRequired(),
});

const targetRequired = Yup.string().when("type", {
    is: (val) => val !== "HL7",
    then: (schema) => schema.required("Delivery Target is a required field"),
    otherwise: (schema) => schema.notRequired(),
});

const hl7TemplateRequired = Yup.string().when("type", {
    is: "HL7",
    then: (schema) => schema.required("HL7 template is required"),
    otherwise: (schema) => schema.notRequired(),
});

const PEM_BLOCK = /-----BEGIN [A-Z0-9 ]+-----[\s\S]+-----END [A-Z0-9 ]+-----/;
const PRIVATE_KEY_BLOCK = /-----BEGIN (?:RSA |EC )?PRIVATE KEY-----/;

/** Paste-only input leaves a .p12 holder with nothing to paste, so say why. */
export const PEM_CONVERSION_HINT =
    "If you were given a .p12 or .pfx file, extract the PEM parts first using openssl pkcs12.";

/** Pasting from a Windows editor or an email carries CRLF and sometimes a BOM, both of which Node's TLS layer rejects. */
export const normalizePastedPem = (value) =>
    typeof value === "string" ? value.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").trim() : value;

/** Certificates are only entered when creating, or when replacing them on an existing registry. */
const whenEnteringCertificates = (build) =>
    Yup.string().when(["type", "replacingCertificates"], (type, replacing, schema) =>
        type === "HL7" && replacing ? build(schema) : schema.notRequired()
    );

const hl7CertificateField = (fieldLabel) =>
    whenEnteringCertificates((schema) =>
        schema
            .required(`${fieldLabel} is required`)
            .test(
                "pem-block",
                `${fieldLabel} must be PEM text starting with -----BEGIN CERTIFICATE-----. ${PEM_CONVERSION_HINT}`,
                (value) => !value || PEM_BLOCK.test(value)
            )
            .test(
                "not-a-private-key",
                `${fieldLabel} looks like a private key. Check the certificate and key have not been swapped.`,
                (value) => !value || !PRIVATE_KEY_BLOCK.test(value)
            )
    );

const hl7PrivateKeyField = whenEnteringCertificates((schema) =>
    schema
        .required("Client private key is required")
        .test(
            "pem-block",
            `Client private key must be PEM text starting with -----BEGIN PRIVATE KEY-----. ${PEM_CONVERSION_HINT}`,
            (value) => !value || PEM_BLOCK.test(value)
        )
        .test(
            "is-a-private-key",
            "This looks like a certificate, not a private key. Check the certificate and key have not been swapped.",
            (value) => !value || PRIVATE_KEY_BLOCK.test(value)
        )
);

/** Build the Secrets Manager bundle the HL7 sender expects. */
export const buildHl7SecretPayload = (values) =>
    JSON.stringify({
        cert: normalizePastedPem(values.clientCertificate),
        key: normalizePastedPem(values.clientPrivateKey),
        ca: normalizePastedPem(values.caCertificate),
    });

const sharedValidationSchema = {
    orgId: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Organization",
            mandatory: true
        }),
    name: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Report Delivery Name",
            mandatory: true
        }),
    type: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Report Delivery Type",
            mandatory: true
        }),
    protocol: conditionalValidation(protocolRequired, {
        validation,
        label: "Protocol"
    }),
    hostName: conditionalValidation(hostNameRequired, {
        validation,
        label: "Hostname"
    }),
    template: conditionalValidation(hl7TemplateRequired, {
        validation,
        label: "HL7 Template"
    }),
    replacingCertificates: Yup.boolean(),
    clientCertificate: hl7CertificateField("Client certificate"),
    clientPrivateKey: hl7PrivateKeyField,
    caCertificate: hl7CertificateField("CA certificate"),
    fileType: fileTypeSchema,
    target: conditionalValidation(targetRequired, {
        validation,
        label: "Delivery Target",
        mandatory: false
    }),
    userName: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Username",
        })
};

export const FormikObj = {
    initialValues: {
        id: "",
        orgId: "",
        name: "",
        type: "",
        fileType: ["PDF"],
        protocol: "",
        hostName: "",
        userName: "",
        secret: "",
        target: "",
        messageSpec: "",
        template: "",
        replacingCertificates: true,
        clientCertificate: "",
        clientPrivateKey: "",
        caCertificate: "",
    },
    validationSchema: Yup.object(sharedValidationSchema),
}

export const FormikObjEdit = {
    initialValues: {
        id: "",
        orgId: "",
        name: "",
        type: "",
        fileType: [],
        protocol: "",
        hostName: "",
        userName: "",
        secret: "",
        target: "",
        messageSpec: "",
        template: "",
        replacingCertificates: false,
        clientCertificate: "",
        clientPrivateKey: "",
        caCertificate: "",
    },
    validationSchema: Yup.object(sharedValidationSchema),
}

export const fileTypes = [
    { value: "PDF", label: "PDF" },
    { value: "XML", label: "Florizel XML" },
    { value: "XML_HLBC_PHMS", label: "HLBC PHMS Legacy XML" },
]

/** Fixed fileType sent for HL7 V2 registries (Step Function gate); not shown in the admin multi-select. */
export const hl7FileTypes = ["HL7"];

export const deliveryTypes = [
    { value: "FAX", label: "Fax" },
    { value: "MFT", label: "MFT" },
    { value: "HL7", label: "HL7 V2" },
]

export const protocols = [
    { value: 'SFTP', label: "SFTP"},
    { value: 'HTTPS', label: 'HTTPS'}
]
export const hl7Protocols = [
    { value: "MLLP", label: "MLLP" },
]
export const hl7Templates = [
    { value: "hl7/oru/default.hbs", label: "hl7/oru/default.hbs" },
]
